import React, { useEffect, useState } from 'react';
import './_NewGroup.css';
import { GroupsModel } from '../models/GroupsModel';
import {
    RoleUpsertModel,
    RoleModulePermission,
    ActivityStatus,
    PermissionType,
} from '../models/superbit-api-models';

// React port of Views/SystemManagement/Modals/_NewGroup.cshtml.
// Handles both "add" (no groupId) and "edit" (groupId set). Create and edit both
// post through groupCreate — the server upserts by Id, exactly like the old form
// which always posted to Group/Create.
//
// The module list and its ModuleRoleId GUIDs are baked into the client (they were
// hardcoded in the old .cshtml) because the server returns an empty model on add
// and only the *granted* modules on edit.
type ModuleDef = { name: string; roleId: string; label: string };
const MODULES: ModuleDef[] = [
    { name: 'Leads', roleId: 'b791d318-7b98-4e4f-be1d-e6eb32e49799', label: 'לידים' },
    { name: 'CustomerManagement', roleId: '07ed5366-7e08-40e6-bbc9-5cd3ead2313f', label: 'ניהול לקוחות' },
    { name: 'Home', roleId: '416ca5e4-6220-4497-9d6d-b2787855e8ec', label: 'בית' },
    { name: 'Simulator', roleId: '7ef134ec-99f4-4046-a90d-58d8582042b6', label: 'סימולטור' },
    { name: 'Tasks', roleId: '3591f330-fdb1-451b-a075-a8a2075a900b', label: 'משימות' },
    { name: 'Claims', roleId: 'e9a2c586-004e-47a7-a60f-04ad22ff838a', label: 'תביעות' },
    { name: 'ReportGenerator', roleId: '8dc2e523-9a6b-4504-8e78-dc868c49ab98', label: 'דוחות' },
    { name: 'Renewals', roleId: 'c4150bb3-44eb-475a-8471-ccb54a296e05', label: 'חידושים' },
    { name: 'Signing', roleId: '9884727d-e69f-4c29-a139-9de8c20bdc48', label: 'חיתומים' },
    { name: 'Email', roleId: '453b3530-019d-4a4b-ab42-e6a9405fdd0c', label: 'אימייל' },
    { name: 'Riders', roleId: '342f0d57-4d41-4e3f-857f-2865c868148b', label: 'ריידרים' },
    { name: 'SystemManagement', roleId: '6b719436-bd96-46ef-a4c6-c0d0b3aa6e7f', label: 'ניהול מערכת' },
    { name: 'Whatsapp', roleId: '56c71ea8-21b0-4cbf-9b6d-7aee44f663d2', label: 'וואטסאפ' }
];

// Radios shown per module, matching the old form's add/edit/view icon order.
const PERMISSION_OPTIONS: { value: PermissionType; label: string; icon: string }[] = [
    { value: PermissionType._2, label: 'יצירה ועריכה', icon: 'i-add' }, // 2 = CreateEdit
    { value: PermissionType._1, label: 'עריכה', icon: 'i-edit' }, // 1 = Edit
    { value: PermissionType._0, label: 'צפייה', icon: 'i-view' }, // 0 = View
];

type ModuleState = { checked: boolean; permissionType: PermissionType };

type Props = {
    show: boolean;
    groupId?: string; // undefined = add, set = edit
    onClose: () => void;
    onSaved: () => void;
};

const lc = (v?: string) => (v || '').toLowerCase();

const emptyModuleState = (): { [name: string]: ModuleState } => {
    const state: { [name: string]: ModuleState } = {};
    MODULES.forEach((m) => {
        state[m.name] = { checked: false, permissionType: PermissionType._0 };
    });
    return state;
};

const NewGroup = (props: Props) => {
    const { show, groupId, onClose, onSaved } = props;
    const isEdit = !!groupId;

    const [groupsModel] = useState<GroupsModel>(() => new GroupsModel());

    const [id, setId] = useState<string | undefined>(undefined);
    const [name, setName] = useState<string>('');
    const [status, setStatus] = useState<ActivityStatus>(ActivityStatus._0);
    const [modules, setModules] = useState<{ [name: string]: ModuleState }>(emptyModuleState());

    const [errors, setErrors] = useState<{ [k: string]: string }>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');

    useEffect(() => {
        if (!show) return;
        loadForm();
        setErrors({});
        setServerError('');
    }, [show, groupId]);

    const loadForm = async () => {
        setLoading(true);
        try {
            const model = await groupsModel.groupGet(groupId);
            setId(model ? model.id : undefined);
            setName(model && model.name ? model.name : '');
            setStatus(model && model.status != null ? model.status : ActivityStatus._0);

            // Start from all-unchecked, then turn on whichever modules the server
            // says this role already has (edit mode). Add mode returns none.
            const state = emptyModuleState();
            const granted = model && model.roleModulePermissions ? model.roleModulePermissions : [];
            granted.forEach((p) => {
                const def = MODULES.find((m) => lc(m.name) === lc(p.moduleName));
                if (def) {
                    state[def.name] = {
                        checked: true,
                        permissionType: p.permissionType != null ? p.permissionType : PermissionType._0,
                    };
                }
            });
            setModules(state);
        } catch (e) {
            console.error('Failed to load group form', e);
            setServerError('טעינת הנתונים נכשלה');
        } finally {
            setLoading(false);
        }
    };

    const toggleModule = (moduleName: string) => {
        setModules((prev) => ({
            ...prev,
            [moduleName]: { ...prev[moduleName], checked: !prev[moduleName].checked },
        }));
    };

    const setPermission = (moduleName: string, permissionType: PermissionType) => {
        setModules((prev) => ({
            ...prev,
            [moduleName]: { checked: true, permissionType },
        }));
    };

    const validate = (): boolean => {
        const errs: { [k: string]: string } = {};
        if (!name.trim()) errs.name = 'שדה חובה';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServerError('');
        if (!validate()) return;

        const body = new RoleUpsertModel();
        body.id = id; // undefined on add — the server creates a new role
        body.name = name.trim();
        body.status = status;
        // Submit only the granted (checked) modules, mirroring the old form.
        body.roleModulePermissions = MODULES.filter((m) => modules[m.name].checked).map((m) => {
            const perm = new RoleModulePermission();
            perm.moduleName = m.name;
            perm.moduleRoleId = m.roleId;
            perm.permissionType = modules[m.name].permissionType;
            return perm;
        });

        setSaving(true);
        try {
            await groupsModel.groupCreate(body);
            onSaved();
            onClose();
        } catch (err) {
            console.error('Failed to save group', err);
            setServerError('שמירת הקבוצה נכשלה');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="newgroup-overlay" onClick={handleOverlayClick}>
            <div className="newgroup-modal" dir="rtl">
                <form id="addGroupForm" autoComplete="off" onSubmit={onSubmit}>
                    <div className="header">
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span className="close-modal" />
                        </button>
                        <h3 className="title">{isEdit ? 'עריכת קבוצה' : 'קבוצה חדשה'}</h3>
                    </div>

                    {loading ? (
                        <div className="newgroup-loading">טוען…</div>
                    ) : (
                        <>
                            <div className="create-form__wrappers add-groups-wrapper">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">שם מלא</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        placeholder="שם מלא"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                    {errors.name && <span className="text-danger">{errors.name}</span>}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">סטטוס</span>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={status}
                                        onChange={(e) =>
                                            setStatus(parseInt(e.target.value, 10) as ActivityStatus)
                                        }
                                    >
                                        <option value={ActivityStatus._0}>פעיל</option>
                                        <option value={ActivityStatus._1}>לא פעיל</option>
                                    </select>
                                </div>
                            </div>

                            <div className="boxes-wrapper">
                                {MODULES.map((m) => {
                                    const ms = modules[m.name];
                                    return (
                                        <div className="menuItem" key={m.name}>
                                            <div className="box">
                                                <span className="title">{m.label}</span>
                                                <input
                                                    type="checkbox"
                                                    className="groupCheck"
                                                    id={`${m.name}Check`}
                                                    checked={ms.checked}
                                                    onChange={() => toggleModule(m.name)}
                                                />
                                                <label htmlFor={`${m.name}Check`} />
                                            </div>
                                            {PERMISSION_OPTIONS.map((opt) => (
                                                <label
                                                    className={`icon-radio ${ms.checked ? '' : 'disabled'}`}
                                                    key={opt.value}
                                                    title={opt.label}
                                                >
                                                    <span className="icon">
                                                        <span className={`i ${opt.icon}`} />
                                                    </span>
                                                    <input
                                                        type="radio"
                                                        name={`perm-${m.name}`}
                                                        value={opt.value}
                                                        disabled={!ms.checked}
                                                        checked={ms.checked && ms.permissionType === opt.value}
                                                        onChange={() => setPermission(m.name, opt.value)}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>

                            {serverError && (
                                <div className="newgroup-server-error text-danger">{serverError}</div>
                            )}

                            <div className="action-buttons">
                                <div className="action-buttons__half">
                                    <button
                                        type="submit"
                                        className="button btn-primary"
                                        disabled={saving}
                                    >
                                        {isEdit ? 'שמור' : 'הוסף'}
                                    </button>
                                    <button
                                        type="button"
                                        className="button"
                                        onClick={onClose}
                                        disabled={saving}
                                    >
                                        ביטול
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default NewGroup;
export { };
